import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { EndpointService } from '@app/core';
import { TareaTramite, ReporteDinamico, CategoriaReporte, ReportesCategoria, Filtro } from '@app/shared';
import { result } from 'lodash';

@Injectable()
export class ReportsDataService {
  constructor(private http: HttpClient, private toastr: ToastrService, private api: EndpointService) { }

  /**
   * Consulta las tareas del tramite predeterminado
   *
   * @param {number} idTramite
   * @returns {Observable<TareaTramite[]>}
   * @memberof ReportsDataService
   */
  getTaskTramiteDefault(idTramite: number): Observable<TareaTramite[]> {
    return new Observable(obs => {
      this.http.skipErrorHandler()
      .get<TareaTramite[]>(this.api.get('taskTramiteDefault', [idTramite])).subscribe(
        task => {
          obs.next(task);
        },
        err => {
          obs.error(err);
          if (err.status !== 401 && err.status !== 403) {
            this.toastr.error('Lo sentimos ha ocurrido un error', 'Fallo de operación');
          }
        }
      );
    });
  }

  /**
   * Consulta el reporte de trazabilidad
   *
   * @param {number} idTramite
   * @param {string} fechaCreacion
   * @param {string} fechaFinalizacion
   * @returns {Promise<any>}
   * @memberof ReportsDataService
   */
  getReportTraceability(idTramite: number, fechaCreacion: string, fechaFinalizacion: string): Promise<any> {
    let configData: any;
    const fechaI = fechaCreacion.split('/');
    const fechaF = fechaFinalizacion.split('/');
    configData = {
      id: idTramite,
      fecha_creacion: fechaI[2] + '-' + fechaI[1] + '-' + fechaI[0],
      fecha_finalizacion: fechaF[2] + '-' + fechaF[1] + '-' + fechaF[0]
    };
    return new Promise((resolve, reject) => {
      this.http
        .skipErrorHandler()
        .post(this.api.get('reportTraceability'), configData, {
          observe: 'response'
        })
        .subscribe(
          res => {
            const path = result(res.body, 'path');
            if (!path) {
              this.toastr.error('El reporte no se pudo generar', 'Error de generación');
            }
            resolve(res.body);
          },
          err => {
            reject(err);
            if (err.status !== 401 && err.status !== 403) {
              this.toastr.error('El reporte no se pudo generar', 'Error de generación');
            }
          }
        );
    });
  }

  /**
   * Consulta el reporte por tareas
   *
   * @param {string} nombreTarea
   * @param {string} nombreTramite
   * @returns {Promise<any>}
   * @memberof ReportsDataService
   */
  getReportTask(nombreTarea: string, nombreTramite: string): Promise<any> {
    let configData: any;
    configData = {
      nombre_tarea: nombreTarea,
      nombre_tramite: nombreTramite
    };
    return new Promise((resolve, reject) => {
      this.http
        .skipErrorHandler()
        .post('http://cinco.integracionweb.com.co:8588/api/v1/report_task', configData, {
          observe: 'response'
        })
        .subscribe(
          res => {
            const path = result(res.body, 'path');
            if (!path) {
              this.toastr.error('El reporte no se pudo generar', 'Error de generación');
            }
            resolve(res.body);
          },
          err => {
            reject(err);
            if (err.status !== 401 && err.status !== 403) {
              this.toastr.error('El reporte no se pudo generar', 'Error de generación');
            }
          }
        );
    });
  }

  /**
   * Consulta de los reportes dinamicos
   *
   * @returns {Observable<ReporteDinamico[]>}
   * @memberof ReportsDataService
   */
  getdynamicReports(): Observable<ReporteDinamico[]> {
    return new Observable(obs => {
      this.http.skipErrorHandler()
      .get<ReporteDinamico[]>(this.api.get('dynamicReport')).subscribe(
        task => {
          obs.next(task);
        },
        err => {
          obs.error(err);
          if (err.status !== 401 && err.status !== 403) {
            this.toastr.error('Lo sentimos ha ocurrido un error', 'Fallo de operación');
          }
        }
      );
    });
  }

  /**
   * Genera el reporte dinamico especificado
   *
   * @param {number} idReport
   * @param {*} [filters]
   * @returns {(Promise<{path: string} | boolean>)}
   * @memberof ReportsDataService
   */
  generatedynamicReports(idReport: number, filters?: any[]): Promise<{path: string} | boolean> {
    /*const formData = {
      id: idReport,
    };
    if (filters) {
      formData['filtros'] = filters;
    }*/
    //Agregamos la propiedad filters para cuando se reciba y exista formData.filters sea igualada a filters
    const formData = {
      id: idReport,
      filtros: filters
    };
    return new Promise((resolve, reject) => {
      this.http.skipErrorHandler()
      .post<{path: string}>(this.api.get('dynamicReport'), formData).subscribe(
        rutGenerate => {
          resolve(rutGenerate);
        },
        err => {
          reject(false);
          if (err.status !== 401 && err.status !== 403) {
            this.toastr.error('Lo sentimos ha ocurrido un error', 'Fallo de operación');
          }
        }
      );
    });
  }

  /**
   * Consulta de todas las categorias de reportes
   *
   * @returns {Observable<CategoriaReporte[]>}
   * @memberof ReportsDataService
   */
  getCategoryReport(): Observable<CategoriaReporte[]> {
    return new Observable(obs => {
      this.http.get<CategoriaReporte[]>(this.api.get('categoryReportsInstance')).subscribe(
        categorys => {
          if (categorys && categorys.length) {
            categorys.sort((a, b) => a.id - b.id);
          }
          obs.next(categorys);
        },
        err => {
          obs.error(err);
        }
      );
    });
  }

  /**
   * Consulta de los reportes por categoria
   *
   * @param {number} idCategory
   * @returns {Observable<ReportesCategoria>}
   * @memberof ReportsDataService
   */
  getReportsCategory(idCategory: number): Observable<ReportesCategoria> {
    return new Observable(obs => {
      this.http.get<ReportesCategoria>(this.api.get('categoryReportsInstance', [idCategory])).subscribe(
        category => {
          if (category && category.reportes && category.reportes.length) {
            category.reportes.sort((a, b) => a.id - b.id);
          }
          obs.next(category);
        },
        err => {
          obs.error(err);
        }
      );
    });
  }

  /**
   * Consulta los filtros de un reporte
   *
   * @param {number} idReport
   * @returns {Observable<Filtro>}
   * @memberof ReportsDataService
   */
  getFiltersReports(idReport: number): Observable<Filtro> {
    return new Observable(obs => {
      this.http.get<Filtro>(this.api.get('filterReportParameterInstance', [idReport])).subscribe(
        filter => {
          if (filter && filter.parametros && filter.parametros.length) {
            filter.parametros.sort((a, b) => a.orden - b.orden);
          }
          obs.next(filter);
        },
        err => {
          obs.error(err);
        }
      );
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/reports/shared/reports-data.service.ts